/**
 * Mapa tekstu: z którego kawałka szablonu pochodzi każda litera wygenerowanego napisu.
 *
 * Dzięki niej pogrubienie fragmentu da się zapamiętać jako „pole: data mszy” zamiast „litery 37–49”,
 * więc działa także u innych zmarłych, gdzie data czy nazwa kościoła mają inną długość.
 *
 * Kawałki (segmenty) mają klucze stałe dla danego szablonu - niezależne od danych:
 * - pole:        `f@<pozycja w szablonie>:<wyrażenie>`, np. "f@58:formatMassDate"
 * - stały tekst: `l@<pozycja w szablonie>`, np. "l@0"
 *
 * Tekst musi być identyczny z tym, co rysuje FabricRenderer: parseTextDSL → applyWordSpacing → usunięcie **.
 */

import { applyTransforms } from './transforms';
import { getValueByPath, isTruthy } from './parser';

const CONDITION_START_REGEX = /^\{([?!])(\w+)\}/;
const PLACEHOLDER_START_REGEX = /^\{([^}]+)\}/;

// Ta sama logika podstawienia co w parseTextDSL (krok 3)
function evaluatePlaceholder(expression, data) {
    const parts = expression.split('|');
    const fieldPath = parts[0].trim();
    const transforms = parts.slice(1);

    let value = getValueByPath(data, fieldPath);
    if (value === undefined || value === null) value = '';
    if (transforms.length > 0) value = applyTransforms(String(value), transforms);
    return String(value);
}

/**
 * Szablon → lista segmentów { kind: 'field' | 'literal', key, text } w kolejności występowania.
 */
export function parseTextDSLWithSegments(template, data = {}) {
    const segments = [];
    if (!template) return segments;
    if (typeof template !== 'string') return [{ kind: 'literal', key: 'l@0', text: String(template) }];

    const walk = (source, baseOffset) => {
        let index = 0;
        let literalStart = 0;
        const flushLiteral = (end) => {
            if (end > literalStart) {
                segments.push({ kind: 'literal', key: `l@${baseOffset + literalStart}`, text: source.slice(literalStart, end) });
            }
        };

        while (index < source.length) {
            if (source[index] !== '{') {
                index += 1;
                continue;
            }
            const rest = source.slice(index);

            // {?pole}…{/pole} oraz {!pole}…{/pole}
            const condition = CONDITION_START_REGEX.exec(rest);
            if (condition) {
                const closeTag = `{/${condition[2]}}`;
                const contentStart = index + condition[0].length;
                const closeIndex = source.indexOf(closeTag, contentStart);
                if (closeIndex !== -1) {
                    flushLiteral(index);
                    const value = getValueByPath(data, condition[2]);
                    const visible = condition[1] === '?' ? isTruthy(value) : !isTruthy(value);
                    if (visible) walk(source.slice(contentStart, closeIndex), baseOffset + contentStart);
                    index = closeIndex + closeTag.length;
                    literalStart = index;
                    continue;
                }
            }

            // {pole} / {pole|transformacja}
            const placeholder = PLACEHOLDER_START_REGEX.exec(rest);
            if (placeholder) {
                flushLiteral(index);
                segments.push({
                    kind: 'field',
                    key: `f@${baseOffset + index}:${placeholder[1]}`,
                    text: evaluatePlaceholder(placeholder[1], data),
                });
                index += placeholder[0].length;
                literalStart = index;
                continue;
            }

            index += 1;
        }
        flushLiteral(source.length);
    };

    walk(template, 0);
    return segments;
}

/**
 * Mapa gotowego napisu: tekst taki jak na canvasie + dla każdej litery jej segment i pozycja w segmencie.
 * @returns {{ text: string, owners: {segment: number, offset: number}[], segments: object[] }}
 */
export function buildTextMap(template, data, wordSpacing = 1) {
    const segments = parseTextDSLWithSegments(template, data);
    let chars = [];
    segments.forEach((segment, segmentIndex) => {
        Array.from(segment.text).forEach((char, offset) => {
            chars.push({ char, owner: { segment: segmentIndex, offset } });
        });
    });

    // Odstępy między słowami - jak applyWordSpacing (spacje i tabulatory, bez nowych linii)
    const parsedSpacing = Number(wordSpacing);
    const spacesCount = Number.isFinite(parsedSpacing) ? Math.max(1, Math.floor(parsedSpacing)) : 1;
    if (spacesCount > 1) {
        const spaced = [];
        for (let i = 0; i < chars.length;) {
            if (/[^\S\r\n]/.test(chars[i].char)) {
                const owner = chars[i].owner;
                while (i < chars.length && /[^\S\r\n]/.test(chars[i].char)) i += 1;
                for (let k = 0; k < spacesCount; k += 1) spaced.push({ char: ' ', owner });
            } else {
                spaced.push(chars[i]);
                i += 1;
            }
        }
        chars = spaced;
    }

    // Znaczniki pogrubienia ** - jak parseBoldMarkers (osobno w każdej linii)
    if (chars.some((item, i) => item.char === '*' && chars[i + 1]?.char === '*')) {
        const kept = [];
        const isMarker = (line, i) => line[i]?.char === '*' && line[i + 1]?.char === '*';
        const findMarker = (line, from) => {
            for (let i = from; i < line.length - 1; i += 1) if (isMarker(line, i)) return i;
            return -1;
        };
        const lines = [[]];
        chars.forEach((item) => {
            if (item.char === '\n') lines.push([]);
            else lines[lines.length - 1].push(item);
        });
        const newlines = chars.filter(item => item.char === '\n');

        lines.forEach((line, lineIndex) => {
            const removed = new Set();
            let position = 0;
            while (position < line.length) {
                const open = findMarker(line, position);
                if (open === -1) break;
                removed.add(open).add(open + 1);
                const close = findMarker(line, open + 2);
                if (close === -1) break;
                removed.add(close).add(close + 1);
                position = close + 2;
            }
            line.forEach((item, i) => { if (!removed.has(i)) kept.push(item); });
            if (lineIndex < newlines.length) kept.push(newlines[lineIndex]);
        });
        chars = kept;
    }

    return {
        text: chars.map(item => item.char).join(''),
        owners: chars.map(item => item.owner),
        segments,
    };
}

/**
 * Rozszerza zaznaczenie [start, end) tak, żeby nie przecinało pól - zaczyna się i kończy na granicy pola.
 */
export function expandRangeToFields(textMap, start, end) {
    if (!textMap || start >= end) return { start, end };
    const { owners, segments } = textMap;
    const isField = index => segments[owners[index]?.segment]?.kind === 'field';
    const sameSegment = (a, b) => owners[a] && owners[b] && owners[a].segment === owners[b].segment;

    let expandedStart = start;
    if (isField(expandedStart)) {
        while (expandedStart > 0 && sameSegment(expandedStart - 1, start)) expandedStart -= 1;
    }
    let expandedEnd = end;
    const last = end - 1;
    if (isField(last)) {
        while (expandedEnd < owners.length && sameSegment(expandedEnd, last)) expandedEnd += 1;
    }
    return { start: expandedStart, end: expandedEnd };
}
